import React, {
  useEffect,
  useState
} from 'react';

import axios from 'axios';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import {
  AlertCircle,
  ArrowRight,
  Download,
  ExternalLink,
  FileText,
  Loader2
} from 'lucide-react';

import { Button } from '../components/ui/button';
import ModonLogo from '../components/ModonLogo';


const API = '/api';


export default function StationOriginalPDFViewer() {

  const { id } = useParams();
  const nav = useNavigate();

  const [blobUrl, setBlobUrl] =
    useState('');

  const [meta, setMeta] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


  useEffect(() => {

    let url = '';

    const load = async () => {

      try {

        setLoading(true);
        setError('');

        /*
          First check station-scoped SDS access.
          This also gives us factory/title metadata.
        */
        const metaResponse =
          await axios.get(
            `${API}/station/sds/${id}`,
            {
              withCredentials: true
            }
          );

        setMeta(
          metaResponse.data
        );


        /*
          IMPORTANT:
          This is the SAME original PDF endpoint used
          by the factory PDF viewer.
          No report is rebuilt here.
        */
        const pdfResponse =
          await axios.get(
            `${API}/sds/${id}/pdf`,
            {
              responseType: 'blob',
              withCredentials: true
            }
          );


        if (
          !pdfResponse.data ||
          pdfResponse.data.size === 0
        ) {
          throw new Error(
            'EMPTY_PDF'
          );
        }


        url =
          URL.createObjectURL(
            pdfResponse.data
          );

        setBlobUrl(url);

      } catch (e) {

        const status =
          e?.response?.status;

        if (status === 404) {
          setError('NOT_FOUND');
        } else if (
          status === 401 ||
          status === 403
        ) {
          setError('FORBIDDEN');
        } else {
          setError('LOAD_ERROR');
        }

      } finally {

        setLoading(false);
      }
    };


    load();


    return () => {

      if (url) {
        URL.revokeObjectURL(url);
      }

    };

  }, [id]);


  const safeName = () => {

    const raw =
      meta?.factory?.factoryName ||
      meta?.title ||
      'SDS-Report';

    return String(raw)
      .replace(/[\\/:*?"<>|]+/g, '-')
      .trim();
  };


  const downloadPdf = () => {

    if (!blobUrl) return;

    const a =
      document.createElement('a');

    a.href = blobUrl;

    a.download =
      `${safeName()}_SDS.pdf`;

    document.body.appendChild(a);

    a.click();

    a.remove();
  };


  const openPdf = () => {

    if (!blobUrl) return;

    window.open(
      blobUrl,
      '_blank',
      'noopener,noreferrer'
    );
  };


  return (
    <div
      dir="rtl"
      className="min-h-screen bg-slate-300 flex flex-col"
    >

      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3 shadow-sm z-10">

        <div className="flex items-center gap-3 min-w-0">

          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              nav('/station')
            }
          >
            <ArrowRight size={14}/>
            العودة
          </Button>


          <ModonLogo size={36}/>


          <div className="min-w-0">

            <div className="font-bold text-slate-800 truncate">
              <FileText
                size={14}
                className="inline ml-1 text-[#1a4d80]"
              />

              {
                meta?.title ||
                'ملف SDS'
              }
            </div>


            {meta?.factory?.factoryName && (
              <div className="text-xs text-slate-500 truncate">
                {
                  meta.factory.factoryName
                }
              </div>
            )}

          </div>

        </div>


        <div className="flex items-center gap-2">

          <Button
            size="sm"
            variant="outline"
            onClick={openPdf}
            disabled={!blobUrl}
          >
            <ExternalLink size={14}/>
            فتح
          </Button>


          <Button
            size="sm"
            variant="outline"
            onClick={downloadPdf}
            disabled={!blobUrl}
          >
            <Download size={14}/>
            تحميل
          </Button>

        </div>

      </header>


      <main className="flex-1 relative">

        {loading ? (

          <div className="absolute inset-0 flex items-center justify-center bg-slate-200">

            <div className="bg-white rounded-xl shadow-lg px-6 py-4 flex items-center gap-2">

              <Loader2
                className="animate-spin text-[#1a4d80]"
                size={22}
              />

              <span>
                جارٍ تحميل ملف المصنع الأصلي...
              </span>

            </div>

          </div>

        ) : error === 'NOT_FOUND' ? (

          <div className="absolute inset-0 flex items-center justify-center p-4">

            <div className="bg-white rounded-xl p-8 shadow-lg max-w-md text-center">

              <AlertCircle
                className="mx-auto text-amber-500 mb-3"
                size={48}
              />

              <h2 className="font-bold text-lg">
                ملف PDF الأصلي غير متوفر
              </h2>

              <p className="text-sm text-slate-600 mt-2">
                التقرير موجود، ولكن المصنع لم يحفظ ملف PDF لهذا التقرير بعد.
              </p>

              <Button
                className="mt-4"
                variant="outline"
                onClick={() =>
                  nav('/station')
                }
              >
                العودة للوحة المحطة
              </Button>

            </div>

          </div>

        ) : error === 'FORBIDDEN' ? (

          <div className="absolute inset-0 flex items-center justify-center">

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">

              <AlertCircle
                className="mx-auto text-red-500 mb-3"
                size={48}
              />

              <h2 className="font-bold">
                غير مصرح بعرض هذا التقرير
              </h2>

              <p className="text-sm text-slate-500 mt-2">
                التقرير لا يتبع مصانع هذه المحطة.
              </p>

            </div>

          </div>

        ) : error ? (

          <div className="absolute inset-0 flex items-center justify-center">

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">

              <AlertCircle
                className="mx-auto text-red-500 mb-3"
                size={48}
              />

              <h2 className="font-bold">
                تعذر تحميل ملف PDF
              </h2>

              <Button
                className="mt-4"
                variant="outline"
                onClick={() =>
                  window.location.reload()
                }
              >
                إعادة المحاولة
              </Button>

            </div>

          </div>

        ) : blobUrl ? (

          <object
            data={blobUrl}
            type="application/pdf"
            className="absolute inset-0 w-full h-full"
          >

            <iframe
              src={blobUrl}
              title="SDS PDF"
              className="absolute inset-0 w-full h-full border-0"
            />

          </object>

        ) : null}

      </main>

    </div>
  );
}
